import React, { useState, useEffect } from 'react';
import {
  Plus, Edit2, Trash2, X, Check, Eye, EyeOff,
  Wrench, Droplets, Settings, Zap, Wind, CircleDot, Shield, Star,
  Car, Package, Layers, Tag, AlertTriangle
} from 'lucide-react';
import TopBar from '../components/layout/TopBar';
import {
  getServices, createService, updateService, deleteService,
  getCarBrands, createCarBrand, updateCarBrand, deleteCarBrand,
  getPartsBrands, createPartsBrand, updatePartsBrand, deletePartsBrand,
} from '../api';
import './CataloguePage.css';

/* ── Constants ── */
const ICON_OPTIONS = ['Wrench','Settings','Droplets','Zap','Wind','CircleDot','Shield','Star'];
const ICON_MAP = {
  Wrench:<Wrench size={18}/>, Settings:<Settings size={18}/>, Droplets:<Droplets size={18}/>,
  Zap:<Zap size={18}/>, Wind:<Wind size={18}/>, CircleDot:<CircleDot size={18}/>,
  Shield:<Shield size={18}/>, Star:<Star size={18}/>,
};
const SVC_CATEGORIES  = ['Mechanical','Wash & Detail','Electrical','Tyres & Wheels'];
const PART_CATEGORIES = ['Filters','Oils','Brakes','AC','Engine','Exterior','Steering','Tyres'];
const PRESET_COLORS   = ['#1C69D4','#222222','#BB0A30','#CC0000','#EB0A1E','#002C5F','#005BAC','#001E50','#00539F','#D52B1E','#E20000','#007A33','#003087','#E60026','#E21A1A'];
const BG_PRESETS      = ['#FFF0F0','#F0FFF4','#F0F4FF','#FFFBF0','#F5F0FF','#FFF0FB','#F0FFFF'];

/* ── Shared sub-components ── */
function ColorPicker({ label, value, onChange }) {
  return (
    <div>
      <label className="label">{label}</label>
      <div className="color-grid">
        {PRESET_COLORS.map(c => (
          <button key={c} type="button" className={`color-swatch ${value===c?'color-swatch--active':''}`}
            style={{ background:c }} onClick={() => onChange(c)}/>
        ))}
      </div>
      <div className="color-row">
        <input type="color" className="color-native" value={value} onChange={e=>onChange(e.target.value)}/>
        <input className="input" style={{ flex:1 }} value={value} onChange={e=>onChange(e.target.value)} placeholder="#007BFF"/>
        <div className="color-preview" style={{ background:value }}/>
      </div>
    </div>
  );
}

function BgPicker({ label, value, onChange }) {
  return (
    <div>
      <label className="label">{label}</label>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:8 }}>
        {BG_PRESETS.map(c => (
          <button key={c} type="button" className={`color-swatch ${value===c?'color-swatch--active':''}`}
            style={{ background:c, border:'1px solid #ddd' }} onClick={() => onChange(c)}/>
        ))}
      </div>
      <div className="color-row">
        <input type="color" className="color-native" value={value} onChange={e=>onChange(e.target.value)}/>
        <input className="input" style={{ flex:1 }} value={value} onChange={e=>onChange(e.target.value)} placeholder="#F0F4FF"/>
        <div className="color-preview" style={{ background:value }}/>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   TAB 1 — SERVICES
══════════════════════════════════════════ */
const BLANK_SVC = { name:'', category:'Mechanical', price:'', duration:'', icon:'Wrench', active:true, tag:'', desc:'', description:'', includes:'', imageUrl:'' };

function ServicesTab() {
  const [services,  setServices]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modal,     setModal]     = useState(false);
  const [editItem,  setEditItem]  = useState(null);
  const [form,      setForm]      = useState(BLANK_SVC);
  const [filterCat, setFilterCat] = useState('All');

  useEffect(() => { getServices().then(s => { setServices(s); setLoading(false); }); }, []);

  const upd = (f,v) => setForm(p => ({ ...p, [f]:v }));
  const openAdd  = () => { setEditItem(null); setForm(BLANK_SVC); setModal(true); };
  const openEdit = s  => { setEditItem(s); setForm({ ...s, desc: s.description||s.desc||'', includes: Array.isArray(s.includes)?s.includes.join(','):(s.includes||''), imageUrl: s.imageUrl||'' }); setModal(true); };
  const handleToggle = s => {
    const u = {
      name:        s.name,
      category:    s.category,
      price:       Number(s.price),
      duration:    s.duration || '',
      icon:        s.icon || 'Wrench',
      active:      !s.active,
      tag:         s.tag || '',
      description: s.description || s.desc || '',
      includes:    Array.isArray(s.includes) ? s.includes : [],
      imageUrl:    s.imageUrl || '',
    };
    updateService(s.id, u).then(() =>
      setServices(p => p.map(x => x.id === s.id ? { ...x, active: !s.active } : x))
    );
  };
  const handleDelete = id => deleteService(id).then(() => setServices(p => p.filter(x => x.id!==id)));
  const handleSave = async () => {
    if (!form.name.trim() || !form.category || !form.price) return;
    // Send ONLY the exact fields the backend Servicedto.Request expects.
    // Never spread ...form — it sends id, createdAt, updatedAt, desc etc.
    // which confuse Spring's @Valid and cause "must not be blank" errors.
    const data = {
      name:        form.name.trim(),
      category:    form.category,
      price:       Number(form.price),
      duration:    form.duration || '',
      icon:        form.icon || 'Wrench',
      active:      Boolean(form.active),
      tag:         form.tag || '',
      description: form.desc || form.description || '',
      includes:    typeof form.includes === 'string'
        ? form.includes.split(',').map(s => s.trim()).filter(Boolean)
        : (Array.isArray(form.includes) ? form.includes : []),
      imageUrl:    form.imageUrl || '',
    };
    try {
      if (editItem) {
        const updated = await updateService(editItem.id, data);
        setServices(p => p.map(x => x.id === editItem.id ? { ...x, ...(updated || data) } : x));
      } else {
        const created = await createService(data);
        setServices(p => [...p, created || { ...data, id: Date.now().toString() }]);
      }
      setModal(false);
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.message || e.message));
    }
  };

  const filtered = filterCat==='All' ? services : services.filter(s=>s.category===filterCat);

  return (
    <div>
      <div className="cat-tab-hdr">
        <div>
          <h2 className="cat-tab-hdr__title">Service Catalogue</h2>
          <p className="cat-tab-hdr__sub">{services.filter(s=>s.active).length} active · {services.filter(s=>!s.active).length} hidden · {services.length} total</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={15}/> Add Service</button>
      </div>

      <div className="cat-filters">
        {['All',...SVC_CATEGORIES].map(c => (
          <button key={c} className={`filter-chip ${filterCat===c?'active':''}`} onClick={() => setFilterCat(c)}>{c}</button>
        ))}
      </div>

      {loading ? (
        <div className="cat-grid">{[1,2,3,4,5,6].map(i=><div key={i} className="cat-skeleton"/>)}</div>
      ) : (
        <div className="cat-grid stagger">
          {filtered.map(s => (
            <div key={s.id} className={`svcm-card card afu ${!s.active?'svcm-card--hidden':''}`}>
              <div className="svcm-card__top">
                <div className="svcm-card__icon">{ICON_MAP[s.icon]||<Wrench size={18}/>}</div>
                <div className="svcm-card__meta">
                  {s.tag && <span className="svcm-tag">{s.tag}</span>}
                  <span className={`badge ${s.active?'badge-success':'badge-neutral'}`}>{s.active?'Visible':'Hidden'}</span>
                </div>
              </div>
              <h3 className="svcm-card__name">{s.name}</h3>
              <p className="svcm-card__category">{s.category}</p>
              <p className="svcm-card__desc">{s.desc}</p>
              <div className="svcm-card__includes">
                {(Array.isArray(s.includes)?s.includes:(s.includes||'').split(',')).filter(Boolean).slice(0,3).map(i=>(
                  <span key={i} className="svcm-include-tag">{i.trim()}</span>
                ))}
              </div>
              <div className="svcm-card__footer">
                <div>
                  <span className="svcm-card__price">₹{Number(s.price).toLocaleString('en-IN')}</span>
                  <span className="svcm-card__duration"> · {s.duration}</span>
                </div>
                <div className="svcm-card__actions">
                  <button className="tbl-btn" title={s.active?'Hide':'Show'} onClick={() => handleToggle(s)}>
                    {s.active?<EyeOff size={13}/>:<Eye size={13}/>}
                  </button>
                  <button className="tbl-btn" onClick={() => openEdit(s)}><Edit2 size={13}/></button>
                  <button className="tbl-btn tbl-btn--danger" onClick={() => handleDelete(s.id)}><Trash2 size={13}/></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" style={{ maxWidth:600 }} onClick={e=>e.stopPropagation()}>
            <div className="modal__header">
              <h3 className="modal__title">{editItem?'Edit Service':'Add New Service'}</h3>
              <button className="modal__close" onClick={() => setModal(false)}><X size={18}/></button>
            </div>
            <div className="modal-form-grid">
              <div className="mfg-full">
                <label className="label">Service Name *</label>
                <input className="input" placeholder="Full Service Package" value={form.name} onChange={e=>upd('name',e.target.value)}/>
              </div>
              <div>
                <label className="label">Category</label>
                <select className="input" value={form.category} onChange={e=>upd('category',e.target.value)}>
                  {SVC_CATEGORIES.map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Icon</label>
                <div className="icon-picker">
                  {ICON_OPTIONS.map(i => (
                    <button key={i} type="button" className={`icon-pick-btn ${form.icon===i?'icon-pick-btn--active':''}`}
                      onClick={() => upd('icon',i)} title={i}>
                      {ICON_MAP[i]}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label">Price (₹) *</label>
                <input className="input" type="number" placeholder="4999" value={form.price} onChange={e=>upd('price',e.target.value)}/>
              </div>
              <div>
                <label className="label">Duration</label>
                <input className="input" placeholder="2–3 hrs" value={form.duration} onChange={e=>upd('duration',e.target.value)}/>
              </div>
              <div>
                <label className="label">Badge Tag</label>
                <input className="input" placeholder="Most Popular" value={form.tag} onChange={e=>upd('tag',e.target.value)}/>
              </div>
              <div>
                <label className="label">Visible to Customers</label>
                <div style={{ display:'flex', gap:8, marginTop:6 }}>
                  {[true,false].map(v=>(
                    <button key={String(v)} type="button" className={`filter-chip ${form.active===v?'active':''}`}
                      onClick={() => upd('active',v)}>{v?'Yes – Show':'No – Hide'}</button>
                  ))}
                </div>
              </div>
              <div className="mfg-full">
                <label className="label">Description</label>
                <textarea className="input" rows={2} style={{ resize:'vertical' }} placeholder="Brief description" value={form.desc} onChange={e=>upd('desc',e.target.value)}/>
              </div>
              <div className="mfg-full">
                <label className="label">Includes <span style={{ fontSize:10, color:'var(--text-muted)', textTransform:'none', letterSpacing:0 }}>(comma-separated)</span></label>
                <input className="input" placeholder="Engine oil & filter, Air filter, Brake inspection" value={form.includes} onChange={e=>upd('includes',e.target.value)}/>
                <p className="modal-hint">These appear as checklist tags on the service card.</p>
              </div>
              <div className="mfg-full">
                <label className="label">Service Image URL <span style={{ fontSize:10, color:'var(--text-muted)', textTransform:'none', letterSpacing:0 }}>(optional — paste a public image link)</span></label>
                <input className="input" placeholder="https://images.unsplash.com/... or /images/services/full-service.jpg" value={form.imageUrl||''} onChange={e=>upd('imageUrl',e.target.value)}/>
                {form.imageUrl && (
                  <div style={{ marginTop:8, borderRadius:8, overflow:'hidden', height:120, background:'var(--bg-secondary)' }}>
                    <img src={form.imageUrl} alt="Preview" style={{ width:'100%', height:'100%', objectFit:'cover' }}
                      onError={e=>{ e.target.style.display='none'; }}/>
                  </div>
                )}
                <p className="modal-hint">This image shows on service cards in the customer app.</p>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}><Check size={14}/> {editItem?'Save Changes':'Add Service'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   TAB 2 — CAR BRANDS
══════════════════════════════════════════ */

/* ── BrandBadge — used in manage cards and preview ── */
function BrandBadge({ imageUrl, abbr, color, name, size = 32 }) {
  const [ok, setOk] = React.useState(true);
  React.useEffect(() => setOk(true), [imageUrl]);
  const show = imageUrl && ok;
  return (
    <div className="brand-manage-card__badge"
      style={{ background: show ? '#fff' : color, overflow:'hidden', padding: show ? 2 : 0 }}>
      {show
        ? <img src={imageUrl} alt={name} style={{ width:'100%', height:'100%', objectFit:'contain' }}
            onError={() => setOk(false)}/>
        : abbr}
    </div>
  );
}

const BLANK_BRAND = { name:'', abbr:'', color:'#1C69D4', imageUrl:'' };

function CarBrandsTab() {
  const [brands,  setBrands]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [editItem,setEditItem]= useState(null);
  const [form,    setForm]    = useState(BLANK_BRAND);

  useEffect(() => { getCarBrands().then(b => { setBrands(b); setLoading(false); }); }, []);

  const upd = (f,v) => setForm(p => ({ ...p, [f]:v }));
  const openAdd  = () => { setEditItem(null); setForm(BLANK_BRAND); setModal(true); };
  const openEdit = b  => { setEditItem(b); setForm({ ...b, imageUrl: b.imageUrl||'' }); setModal(true); };
  const handleDelete = id => deleteCarBrand(id).then(() => setBrands(p=>p.filter(x=>x.id!==id)));
  const handleSave = () => {
    if (!form.name.trim()||!form.abbr.trim()) return;
    if (editItem) {
      updateCarBrand(editItem.id, form).then(() => { setBrands(p=>p.map(x=>x.id===editItem.id?{...x,...form}:x)); setModal(false); });
    } else {
      createCarBrand(form).then(n => { setBrands(p=>[...p,n]); setModal(false); });
    }
  };

  return (
    <div>
      <div className="cat-tab-hdr">
        <div>
          <h2 className="cat-tab-hdr__title">Car Brands</h2>
          <p className="cat-tab-hdr__sub">{brands.length} brands · shown as chips on customer home page</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={15}/> Add Brand</button>
      </div>

      {loading ? (
        <div className="cat-brands-grid">{[1,2,3,4,5,6,7,8].map(i=><div key={i} className="cat-skeleton cat-skeleton--sm"/>)}</div>
      ) : (
        <div className="cat-brands-grid stagger">
          {brands.map(b => (
            <div key={b.id} className="brand-manage-card afu">
              <BrandBadge imageUrl={b.imageUrl} abbr={b.abbr} color={b.color} name={b.name}/>
              <div className="brand-manage-card__info"><p className="brand-manage-card__name">{b.name}</p></div>
              <div className="brand-manage-card__actions">
                <button className="tbl-btn" onClick={() => openEdit(b)}><Edit2 size={12}/></button>
                <button className="tbl-btn tbl-btn--danger" onClick={() => handleDelete(b.id)}><Trash2 size={12}/></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" style={{ maxWidth:420 }} onClick={e=>e.stopPropagation()}>
            <div className="modal__header">
              <h3 className="modal__title">{editItem?'Edit Car Brand':'Add Car Brand'}</h3>
              <button className="modal__close" onClick={() => setModal(false)}><X size={18}/></button>
            </div>
            <div className="modal-form-grid">
              <div>
                <label className="label">Brand Name *</label>
                <input className="input" placeholder="e.g. Toyota" value={form.name} onChange={e=>upd('name',e.target.value)}/>
              </div>
              <div>
                <label className="label">Abbreviation * (max 4)</label>
                <input className="input" placeholder="T" maxLength={4} value={form.abbr} onChange={e=>upd('abbr',e.target.value.toUpperCase())}/>
                <p className="modal-hint">Shown inside the colour badge.</p>
              </div>
              <div className="mfg-full">
                <ColorPicker label="Brand Colour" value={form.color} onChange={v=>upd('color',v)}/>
              </div>
              <div className="mfg-full">
                <label className="label">Brand Logo URL <span style={{ fontSize:10, color:'var(--text-muted)', textTransform:'none', letterSpacing:0, fontWeight:400 }}>(optional — replaces colour badge)</span></label>
                <input className="input" placeholder="https://... or /images/brands/bmw.png" value={form.imageUrl||''} onChange={e=>upd('imageUrl',e.target.value)}/>
                {form.imageUrl && (
                  <div style={{ marginTop:8, width:60, height:60, borderRadius:8, overflow:'hidden', background:'var(--bg-secondary)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <img src={form.imageUrl} alt="Preview" style={{ width:'100%', height:'100%', objectFit:'contain' }}
                      onError={e=>{ e.target.style.display='none'; }}/>
                  </div>
                )}
              </div>
              <div className="mfg-full">
                <label className="label">Preview</label>
                <div className="brand-preview">
                  <BrandBadge imageUrl={form.imageUrl} abbr={form.abbr||'?'} color={form.color} name={form.name}/>
                  <span className="brand-preview__name">{form.name||'Brand Name'}</span>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}><Check size={14}/> {editItem?'Save Changes':'Add Brand'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   TAB 3 — SPARE PARTS BRANDS
══════════════════════════════════════════ */
const BLANK_PART_BRAND = { name:'', desc:'', abbr:'', color:'#E20000', bg:'#FFF0F0' };

function PartsBrandsTab() {
  const [parts,   setParts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [editItem,setEditItem]= useState(null);
  const [form,    setForm]    = useState(BLANK_PART_BRAND);

  useEffect(() => { getPartsBrands().then(p => { setParts(p); setLoading(false); }); }, []);

  const upd = (f,v) => setForm(p => ({ ...p, [f]:v }));
  const openAdd  = () => { setEditItem(null); setForm(BLANK_PART_BRAND); setModal(true); };
  const openEdit = p  => { setEditItem(p); setForm({ ...p }); setModal(true); };
  const handleDelete = id => deletePartsBrand(id).then(() => setParts(p=>p.filter(x=>x.id!==id)));
  const handleSave = () => {
    if (!form.name.trim()||!form.abbr.trim()) return;
    if (editItem) {
      updatePartsBrand(editItem.id, form).then(() => { setParts(p=>p.map(x=>x.id===editItem.id?{...x,...form}:x)); setModal(false); });
    } else {
      createPartsBrand(form).then(n => { setParts(p=>[...p,n]); setModal(false); });
    }
  };

  return (
    <div>
      <div className="cat-tab-hdr">
        <div>
          <h2 className="cat-tab-hdr__title">Spare Parts Brands</h2>
          <p className="cat-tab-hdr__sub">{parts.length} brands · shown on customer home page</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={15}/> Add Parts Brand</button>
      </div>

      {loading ? (
        <div className="cat-parts-grid">{[1,2,3,4,5,6,7,8].map(i=><div key={i} className="cat-skeleton cat-skeleton--part"/>)}</div>
      ) : (
        <div className="cat-parts-grid stagger">
          {parts.map(p => (
            <div key={p.id} className="parts-manage-card afu">
              <div className="parts-manage-card__icon" style={{ background:p.bg }}>
                <span style={{ color:p.color, fontSize:11, fontWeight:800 }}>{p.abbr}</span>
              </div>
              <div className="parts-manage-card__info">
                <p className="parts-manage-card__name">{p.name}</p>
                <p className="parts-manage-card__desc">{p.desc}</p>
              </div>
              <div className="parts-manage-card__actions">
                <button className="tbl-btn" onClick={() => openEdit(p)}><Edit2 size={12}/></button>
                <button className="tbl-btn tbl-btn--danger" onClick={() => handleDelete(p.id)}><Trash2 size={12}/></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" style={{ maxWidth:460 }} onClick={e=>e.stopPropagation()}>
            <div className="modal__header">
              <h3 className="modal__title">{editItem?'Edit Parts Brand':'Add Parts Brand'}</h3>
              <button className="modal__close" onClick={() => setModal(false)}><X size={18}/></button>
            </div>
            <div className="modal-form-grid">
              <div>
                <label className="label">Brand Name *</label>
                <input className="input" placeholder="e.g. Bosch" value={form.name} onChange={e=>upd('name',e.target.value)}/>
              </div>
              <div>
                <label className="label">Category / Supplies</label>
                <input className="input" placeholder="Filters & Ignition" value={form.desc} onChange={e=>upd('desc',e.target.value)}/>
              </div>
              <div>
                <label className="label">Abbreviation * (max 4)</label>
                <input className="input" placeholder="B" maxLength={4} value={form.abbr} onChange={e=>upd('abbr',e.target.value.toUpperCase())}/>
              </div>
              <div/>
              <div className="mfg-full">
                <ColorPicker label="Icon Text Colour" value={form.color} onChange={v=>upd('color',v)}/>
              </div>
              <div className="mfg-full">
                <BgPicker label="Icon Background" value={form.bg} onChange={v=>upd('bg',v)}/>
              </div>
              <div className="mfg-full">
                <label className="label">Brand Logo URL <span style={{ fontSize:10, color:'var(--text-muted)', textTransform:'none', letterSpacing:0, fontWeight:400 }}>(optional — replaces colour badge)</span></label>
                <input className="input" placeholder="https://... or /images/parts/bosch.png" value={form.imageUrl||''} onChange={e=>upd('imageUrl',e.target.value)}/>
                {form.imageUrl && (
                  <div style={{ marginTop:8, width:60, height:60, borderRadius:8, overflow:'hidden', background:form.bg||'var(--bg-secondary)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <img src={form.imageUrl} alt="Preview" style={{ width:'100%', height:'100%', objectFit:'contain' }}
                      onError={e=>{ e.target.style.display='none'; }}/>
                  </div>
                )}
              </div>
              <div className="mfg-full">
                <label className="label">Preview</label>
                <div className="parts-preview">
                  <div className="parts-preview__icon" style={{ background:form.bg, overflow:form.imageUrl?'hidden':undefined }}>
                    {form.imageUrl
                      ? <img src={form.imageUrl} alt={form.name} style={{ width:'100%', height:'100%', objectFit:'contain' }} onError={e=>{e.target.style.display='none';}}/>
                      : <span style={{ color:form.color, fontSize:13, fontWeight:800 }}>{form.abbr||'?'}</span>
                    }
                  </div>
                  <div>
                    <p className="parts-preview__name">{form.name||'Brand Name'}</p>
                    <p className="parts-preview__desc">{form.desc||'Category'}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}><Check size={14}/> {editItem?'Save Changes':'Add Brand'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   TAB 4 — SPARE PARTS PRODUCTS (Inventory)
══════════════════════════════════════════ */

// localStorage key matching InventoryPage
const INV_KEY = 'ae_inventory';

const DEFAULT_PRODUCTS = [
  { id:1,  name:'Engine Oil Filter (Universal)', sku:'FLT-EOF-001', category:'Filters',   qty:4,  minQty:5,  unitPrice:450,  supplier:'Bosch India' },
  { id:2,  name:'Cabin Air Filter',              sku:'FLT-CAB-002', category:'Filters',   qty:12, minQty:5,  unitPrice:320,  supplier:'Denso India' },
  { id:3,  name:'Synthetic Oil 5W30 4L',         sku:'OIL-5W30-4L', category:'Oils',      qty:18, minQty:10, unitPrice:2200, supplier:'Castrol India' },
  { id:4,  name:'Synthetic Oil 5W40 4L',         sku:'OIL-5W40-4L', category:'Oils',      qty:14, minQty:10, unitPrice:2400, supplier:'Mobil India' },
  { id:5,  name:'Brake Fluid DOT4 500ml',        sku:'BRK-FLD-001', category:'Brakes',    qty:6,  minQty:5,  unitPrice:280,  supplier:'Brembo' },
  { id:6,  name:'Front Brake Pads (Set)',         sku:'BRK-PAD-F01', category:'Brakes',    qty:3,  minQty:4,  unitPrice:1800, supplier:'Brembo' },
  { id:7,  name:'AC Refrigerant R134a 250g',     sku:'AC-REF-250',  category:'AC',        qty:9,  minQty:6,  unitPrice:180,  supplier:'Honeywell' },
  { id:8,  name:'Spark Plugs Iridium (Set of 4)',sku:'ENG-SPK-IR4', category:'Engine',    qty:7,  minQty:4,  unitPrice:1200, supplier:'NGK India' },
  { id:9,  name:'Wiper Blades (Pair)',            sku:'EXT-WPR-001', category:'Exterior',  qty:14, minQty:6,  unitPrice:480,  supplier:'Bosch India' },
  { id:10, name:'Power Steering Fluid 1L',       sku:'STR-PSF-001', category:'Steering',  qty:2,  minQty:4,  unitPrice:350,  supplier:'OEM Parts' },
];

const BLANK_PRODUCT = { name:'', sku:'', category:'Filters', qty:0, minQty:5, unitPrice:'', supplier:'' };

function loadProducts() {
  try { const s=localStorage.getItem(INV_KEY); return s?JSON.parse(s):DEFAULT_PRODUCTS; } catch { return DEFAULT_PRODUCTS; }
}
function saveProducts(p) { try { localStorage.setItem(INV_KEY,JSON.stringify(p)); } catch {} }

function ProductsTab() {
  const [products,  setProducts]  = useState(loadProducts);
  const [modal,     setModal]     = useState(false);
  const [editItem,  setEditItem]  = useState(null);
  const [form,      setForm]      = useState(BLANK_PRODUCT);
  const [filterCat, setFilterCat] = useState('All');
  const [search,    setSearch]    = useState('');


  const persist = (updated) => { setProducts(updated); saveProducts(updated); };
  const upd = (f,v) => setForm(p => ({ ...p, [f]:v }));
  const openAdd  = () => { setEditItem(null); setForm(BLANK_PRODUCT); setModal(true); };
  const openEdit = p  => { setEditItem(p); setForm({ ...p, unitPrice:p.unitPrice }); setModal(true); };

  const handleDelete = id => persist(products.filter(x=>x.id!==id));
  const handleSave = () => {
    if (!form.name.trim()||!form.sku.trim()||!form.unitPrice) return;
    const data = { ...form, qty:Number(form.qty), minQty:Number(form.minQty), unitPrice:Number(form.unitPrice) };
    if (editItem) {
      persist(products.map(x=>x.id===editItem.id?{...x,...data}:x));
    } else {
      persist([...products, { ...data, id:Date.now() }]);
    }
    setModal(false);
  };

  const adjustStock = (id, delta) => {
    persist(products.map(p => {
      if (p.id!==id) return p;
      const newQty = Math.max(0, p.qty + delta);
      return { ...p, qty:newQty };
    }));
  };

  const filtered = products
    .filter(p => filterCat==='All'||p.category===filterCat)
    .filter(p => !search || [p.name,p.sku,p.supplier].join(' ').toLowerCase().includes(search.toLowerCase()));

  const lowCount = products.filter(p=>p.qty<=p.minQty).length;

  return (
    <div>
      <div className="cat-tab-hdr">
        <div>
          <h2 className="cat-tab-hdr__title">Spare Parts Products</h2>
          <p className="cat-tab-hdr__sub">
            {products.length} items
            {lowCount>0 && <span className="low-stock-badge"><AlertTriangle size={11}/> {lowCount} low stock</span>}
          </p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={15}/> Add Part</button>
      </div>

      {/* Toolbar */}
      <div className="products-toolbar">
        <div className="products-search-wrap">
          <input className="input" placeholder="Search name, SKU, supplier…"
            value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <div className="cat-filters" style={{ margin:0 }}>
          {['All',...PART_CATEGORIES].map(c=>(
            <button key={c} className={`filter-chip ${filterCat===c?'active':''}`} onClick={()=>setFilterCat(c)}>{c}</button>
          ))}
        </div>
      </div>

      {/* Products grid */}
      <div className="products-grid stagger">
        {filtered.map(p => {
          const isLow = p.qty <= p.minQty;
          return (
            <div key={p.id} className={`product-card card afu ${isLow?'product-card--low':''}`}>
              {isLow && (
                <div className="product-card__low-badge">
                  <AlertTriangle size={11}/> Low Stock
                </div>
              )}
              <div className="product-card__header">
                <span className="product-card__sku">{p.sku}</span>
                <span className="badge badge-neutral">{p.category}</span>
              </div>
              <h3 className="product-card__name">{p.name}</h3>
              <p className="product-card__supplier">{p.supplier}</p>

              {/* Stock bar */}
              <div className="product-card__stock">
                <div className="product-card__stock-bar">
                  <div className="product-card__stock-fill"
                    style={{
                      width:`${Math.min(100,(p.qty/Math.max(p.minQty*2,1))*100)}%`,
                      background: isLow ? 'var(--danger)' : 'var(--success)'
                    }}/>
                </div>
                <span className={`product-card__qty ${isLow?'product-card__qty--low':''}`}>
                  {p.qty} / min {p.minQty}
                </span>
              </div>

              <div className="product-card__footer">
                <span className="product-card__price">₹{Number(p.unitPrice).toLocaleString('en-IN')}</span>
                <div className="product-card__actions">
                  {/* Stock +/- controls */}
                  <div className="stock-controls">
                    <button className="stock-btn" onClick={() => adjustStock(p.id,-1)} disabled={p.qty===0}>−</button>
                    <span className="stock-qty">{p.qty}</span>
                    <button className="stock-btn" onClick={() => adjustStock(p.id,1)}>+</button>
                  </div>
                  <button className="tbl-btn" onClick={() => openEdit(p)}><Edit2 size={12}/></button>
                  <button className="tbl-btn tbl-btn--danger" onClick={() => handleDelete(p.id)}><Trash2 size={12}/></button>
                </div>
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div style={{ gridColumn:'1/-1', textAlign:'center', padding:'40px 20px', color:'var(--text-muted)', fontSize:13 }}>
            No parts found. <button className="btn btn-ghost btn-sm" onClick={openAdd}><Plus size={13}/> Add Part</button>
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" style={{ maxWidth:560 }} onClick={e=>e.stopPropagation()}>
            <div className="modal__header">
              <h3 className="modal__title">{editItem?'Edit Spare Part':'Add Spare Part'}</h3>
              <button className="modal__close" onClick={() => setModal(false)}><X size={18}/></button>
            </div>
            <div className="modal-form-grid">
              <div className="mfg-full">
                <label className="label">Part Name *</label>
                <input className="input" placeholder="e.g. Engine Oil Filter (Universal)" value={form.name} onChange={e=>upd('name',e.target.value)}/>
              </div>
              <div>
                <label className="label">SKU *</label>
                <input className="input" placeholder="FLT-EOF-001" value={form.sku} onChange={e=>upd('sku',e.target.value.toUpperCase())}/>
              </div>
              <div>
                <label className="label">Category</label>
                <select className="input" value={form.category} onChange={e=>upd('category',e.target.value)}>
                  {PART_CATEGORIES.map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Unit Price (₹) *</label>
                <input className="input" type="number" placeholder="450" value={form.unitPrice} onChange={e=>upd('unitPrice',e.target.value)}/>
              </div>
              <div>
                <label className="label">Supplier</label>
                <input className="input" placeholder="Bosch India" value={form.supplier} onChange={e=>upd('supplier',e.target.value)}/>
              </div>
              <div>
                <label className="label">Current Stock (qty)</label>
                <input className="input" type="number" min="0" value={form.qty} onChange={e=>upd('qty',e.target.value)}/>
              </div>
              <div>
                <label className="label">Min Stock Alert (qty)</label>
                <input className="input" type="number" min="0" value={form.minQty} onChange={e=>upd('minQty',e.target.value)}/>
                <p className="modal-hint">Alert shows when stock falls to or below this number.</p>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}><Check size={14}/> {editItem?'Save Changes':'Add Part'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════ */
const TABS = [
  { id:'services', label:'Services',     icon:<Layers size={16}/> },
  { id:'brands',   label:'Car Brands',   icon:<Car size={16}/> },
  { id:'parts',    label:'Parts Brands', icon:<Package size={16}/> },
  { id:'products', label:'Products',     icon:<Tag size={16}/> },
];

export default function CataloguePage() {
  const [activeTab, setActiveTab] = useState('services');
  return (
    <>
      <TopBar title="Catalogue" subtitle="Manage services, car brands, parts brands & spare parts inventory"/>
      <div className="admin-content">
        <div className="cat-tabs">
          {TABS.map(t => (
            <button key={t.id}
              className={`cat-tab-btn ${activeTab===t.id?'cat-tab-btn--active':''}`}
              onClick={() => setActiveTab(t.id)}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
        <div className="cat-content">
          {activeTab==='services'  && <ServicesTab/>}
          {activeTab==='brands'    && <CarBrandsTab/>}
          {activeTab==='parts'     && <PartsBrandsTab/>}
          {activeTab==='products'  && <ProductsTab/>}
        </div>
      </div>
    </>
  );
}